import { FC, useContext, useEffect, useMemo, useState } from 'react'
import { browserName, isMobile } from 'react-device-detect'
import { useAsync } from 'react-use'
import { writeStorage } from '@rehooks/local-storage'
import * as Sentry from '@sentry/browser'
import { ConnectedUser } from '@ucc/admin-global-state'
import { Analytics, GlobalSecurityContext, SecurityContext } from '@ucc/unified-admin-common'
import mixpanel from 'mixpanel-browser'

import { ConnectedUserContext, OrganizationContext } from 'components'
import { useAccountContext } from 'components/contexts/AccountContext'
import config from 'config'
import { analyticsEventNames, convertToAnalytics, fetchIAm } from 'utils'
import { getIsEmployee, getUserAdminRoles } from 'utils/analytics'
import { qualtricsSurveyInit } from 'utils/qualtricsSurvey'
import { buildSecurityContextFromUserAndAccount } from 'utils/rolesToPrivilegesMapper'

const ConnectedUserProvider: FC = ({ children }) => {
  const [connectedUser, setConnectedUser] = useState<ConnectedUser | null>(null)
  const [securityContext, setSecurityContext] = useState<SecurityContext>({})
  const {
    accountKey: selectedAccountKey,
    managedAdminRoles,
    isPartner,
    isSettled,
    partnerAccountKey,
    unifiedAdmin,
    billingRoles,
    goToAdminTime,
  } = useAccountContext()

  const pbx = useContext(OrganizationContext)

  const licenseUtilization = useMemo(
    () =>
      pbx?.licensesDetails?.results
        ?.filter((el) => !!el.seats)
        .reduce((acc, el) => {
          return {
            ...acc,
            [`${el.description.replace(/ /g, '')}_utilization`]: Math.round((el.userCount / el.seats) * 100) / 100,
          }
        }, {}),
    [pbx?.licensesDetails?.results],
  )

  useAsync(async () => {
    const iAm = await fetchIAm()

    setConnectedUser(iAm)

    writeStorage(config.I_AM_TOKEN_KEY, iAm)

    Sentry.setUser({
      id: iAm.key,
    })
  })

  useEffect(() => {
    if (connectedUser && selectedAccountKey) {
      setSecurityContext(
        buildSecurityContextFromUserAndAccount(connectedUser, selectedAccountKey, managedAdminRoles, billingRoles),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingRoles, connectedUser?.key, managedAdminRoles, selectedAccountKey])

  useAsync(async () => {
    // Identify Mixpanel, Segment user

    if (connectedUser && connectedUser.key) {
      const userAdminRolesOnAccount = getUserAdminRoles(connectedUser.accounts, selectedAccountKey)
      const isEmployee = getIsEmployee(connectedUser)

      const analyticsIam = convertToAnalytics({
        ...connectedUser,
        adminRoles: userAdminRolesOnAccount,
        isPartner: isPartner ?? false,
        isEmployee,
      })

      mixpanel.identify(connectedUser.key)

      mixpanel.register({
        ...analyticsIam,
        browserName,
        isMobile,
        totalSeatCount: (pbx?.licensesDetails?.results || []).reduce(
          (accumulator, current) => accumulator + (current.seats || 0),
          0,
        ),
        unifiedAdmin,
        userAgent: navigator.userAgent,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
      })

      if (selectedAccountKey) {
        if (!getIsEmployee(connectedUser)) {
          if (!window.QSI) {
            qualtricsSurveyInit(connectedUser, selectedAccountKey, goToAdminTime)
          }
          if (window.startupQualtricsSurvey) {
            window.startupQualtricsSurvey()
          }
        }
      }

      if (isSettled && pbx?.licensesDetails) {
        Analytics.identify(connectedUser.key, {
          ...convertToAnalytics(
            {
              ...connectedUser,
              adminRoles: userAdminRolesOnAccount,
              isPartner: isPartner ?? false,
              userInACustomerAccount: !!partnerAccountKey,
              isEmployee: isEmployee,
            },
            licenseUtilization,
          ),
          unifiedAdmin,
        })
        Analytics.track(analyticsEventNames.PORTAL, {
          Action: `Loaded the portal`,
        })
      }
    }
  }, [selectedAccountKey, pbx?.licensesDetails?.results, connectedUser?.key, isSettled])

  return (
    <ConnectedUserContext.Provider value={connectedUser}>
      <GlobalSecurityContext.Provider value={securityContext}>{children}</GlobalSecurityContext.Provider>
    </ConnectedUserContext.Provider>
  )
}

export default ConnectedUserProvider
